import React, { useCallback, useEffect, useRef, useState } from 'react';
import useForceUpdateOnWindowResize from '../../../hooks/useForceUpdateOnWindowResize';
import { MODAL_ANIMATION_ENDED_EVENT } from '../ModalV2';

import { Wrapper, Controls, Title, Body } from './ModalContent.styles';

export type ModalContentProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'title' | 'className'
> & {
  title?:
    | {
        /**
         * Optional ID to override the id on the h1 used by the Modal to direct screen readers to the title of the modal.
         *
         * Defaults to dialog-heading to match the Modal default
         */
        id?: string;
        hed: string;
      }
    | React.ReactNode;

  /**
   * Content slot for buttons in the modal.
   */
  controls?: React.ReactNode;

  /**
   * Should the controls align left?  Default: no, the controls should align right
   */
  alignControlsLeft?: boolean;

  /**
   * On mobile, should the controls be stacked vertically
   */
  stackedButtons?: boolean;

  /**
   * On mobile, should the header have sticky styling on full screen modals (box shadow under title).
   */
  stickyHeader?: boolean;

  /**
   * On non-mobile, fix the height of the modal based on the screen size and scroll the body content if over the max height.
   */
  scrollBody?: boolean;

  /**
   * Optional className to apply to the content wrapper.  You may want to consider using emotion css overrides instead.
   */
  className?: string;
};

/**
 * ModalContent is a helper component, for use within a ModalV2 that draws standard zola-ui
 * modal content (header + body + buttons).
 *
 * This component
 *
 * - Controls the maximum height of the modal
 * - Defines the scroll behavior (body should scroll)
 * - Lays out out buttons in standard patterns (right justified on web / left justified on web / stacked on mobile / justified on mobile )
 * - Provides a default (identified) title for the modal's screen reader support
 */
const ModalContent: React.FC<ModalContentProps> = (props): JSX.Element => {
  const {
    children,
    className,
    title,
    controls,
    alignControlsLeft = false,
    stackedButtons = true,
    stickyHeader = false,
    scrollBody = false,
    ...rest
  } = props;
  useForceUpdateOnWindowResize();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [isFullScreen, setIsFullScreen] = useState(false);
  const calculateTopPosition = () => {
    if (wrapperRef.current) {
      const topPosition = wrapperRef.current.getBoundingClientRect().top;
      if (topPosition === 0) {
        setIsFullScreen(true);
      } else {
        setIsFullScreen(false);
      }
    }
  };
  useEffect(calculateTopPosition);

  useEffect(() => {
    window.addEventListener(MODAL_ANIMATION_ENDED_EVENT, calculateTopPosition);
    return () => {
      window.removeEventListener(MODAL_ANIMATION_ENDED_EVENT, calculateTopPosition);
    };
  }, []);

  const renderTitle = useCallback(() => {
    if (title) {
      if (typeof title === 'object' && 'hed' in title) {
        return (
          <Title id={title.id || 'dialog-heading'} sticky={stickyHeader && isFullScreen}>
            {title.hed}
          </Title>
        );
      }
      return title;
    }
    return null;
  }, [title, stickyHeader, isFullScreen]);
  return (
    <Wrapper className={className} ref={wrapperRef} scrollBody={scrollBody} {...rest}>
      {renderTitle()}

      <Body scrollBody={scrollBody}>{children}</Body>

      {controls && (
        <Controls alignControlsLeft={alignControlsLeft} stackedButtons={stackedButtons}>
          {controls}
        </Controls>
      )}
    </Wrapper>
  );
};
export default ModalContent;
